import { formatLanguage, Input, Select, Textarea } from '@platform/design-system';
import { localeDirection, SUPPORTED_LOCALES, useTranslation, type Locale } from '@platform/i18n';
import type { TypedClient } from '@platform/openapi';
import { useFormContext, useWatch } from 'react-hook-form';
import { nameIn, type Category } from '../categories.ts';
import {
  MAX_CODE_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_NAME_LENGTH,
  type ProductFormValues,
} from '../product-form.ts';
import { useCompanies } from '../../companies.ts';
import { subsidiaryOptions } from '../../settings/subsidiaries.ts';
import type { TenantDetail } from '../product.ts';
import type { CatalogOption, OptionIndex } from '../options.ts';
import { EditorSectionCard } from './EditorSectionCard.tsx';
import { OriginField } from './OriginField.tsx';
import styles from './ProductEditor.module.css';
import { useFieldError } from './sections.ts';
import { useReferenceSuggestion } from './useReferenceSuggestion.ts';

type Placement = { readonly rootId: string; readonly childId: string };

const placementOf = (categories: readonly Category[], categoryId: string): Placement => {
  const chosen = categories.find((category) => category.id === categoryId);
  if (chosen === undefined) return { rootId: '', childId: '' };
  return chosen.parentId === null
    ? { rootId: chosen.id, childId: '' }
    : { rootId: chosen.parentId, childId: chosen.id };
};

export type EssentialsSectionProps = {
  api: TypedClient;
  categories: readonly Category[];
  subsidiaries: TenantDetail['subsidiaries'];
  options: readonly CatalogOption[];
  index: OptionIndex;
  nameLocale: Locale;
  onNameLocaleChange: (locale: Locale) => void;
};

export const EssentialsSection = ({
  api,
  categories,
  subsidiaries,
  options,
  index,
  nameLocale,
  onNameLocaleChange,
}: EssentialsSectionProps) => {
  const { t, i18n } = useTranslation();
  const { register, formState, control, setValue } = useFormContext<ProductFormValues>();
  const categoryId = useWatch({ control, name: 'categoryId' });
  const placement = placementOf(categories, categoryId);
  const roots = categories.filter((category) => category.parentId === null);
  const children = categories.filter(
    (category) => category.parentId !== null && category.parentId === placement.rootId,
  );
  const nameOf = (category: Category) => nameIn(category, i18n.language, category.id);
  const currentSubsidiary = useWatch({ control, name: 'subsidiaryId' });
  const companies = useCompanies(api);
  const errorText = useFieldError();
  useReferenceSuggestion({ api, subsidiaries, categories });
  const [translations, defaultLocale] = useWatch<ProductFormValues, ['translations', 'defaultLocale']>({
    name: ['translations', 'defaultLocale'],
  });

  return (
    <EditorSectionCard section="essentials">
      <div className={styles['stack']}>
        <div role="tablist" aria-label={t('productEditor.languages')} className={styles['tabs']}>
          {SUPPORTED_LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              role="tab"
              aria-selected={locale === nameLocale}
              className={[styles['tab'], locale === nameLocale ? styles['activeTab'] : ''].join(' ')}
              onClick={() => {
                onNameLocaleChange(locale);
              }}
            >
              {formatLanguage(locale, i18n.language)}
              {locale !== defaultLocale && translations[locale].name.trim() === '' ? (
                <span className={styles['missing']}>{t('productEditor.translationMissing')}</span>
              ) : null}
            </button>
          ))}
        </div>
        <div role="tabpanel" className={styles['stack']} key={nameLocale}>
          <Input
            label={t('catalog.name')}
            required={nameLocale === defaultLocale}
            dir={localeDirection(nameLocale)}
            lang={nameLocale}
            maxLength={MAX_NAME_LENGTH}
            errorText={errorText(formState.errors.translations?.[nameLocale]?.name)}
            {...register(`translations.${nameLocale}.name`)}
          />
          <Textarea
            label={t('productEditor.description')}
            dir={localeDirection(nameLocale)}
            lang={nameLocale}
            maxLength={MAX_DESCRIPTION_LENGTH}
            {...register(`translations.${nameLocale}.description`)}
          />
        </div>
        <div className={styles['columns']}>
          <Select
            label={t('companies.label')}
            options={[
              { value: '', label: companies.mainLabel },
              ...subsidiaryOptions(subsidiaries, currentSubsidiary),
            ]}
            {...register('subsidiaryId')}
          />
          <Select
            label={t('catalog.category')}
            value={placement.rootId}
            options={[
              { value: '', label: t('productEditor.noCategory') },
              ...roots.map((category) => ({ value: category.id, label: nameOf(category) })),
            ]}
            onChange={(event) => {
              setValue('categoryId', event.target.value, { shouldDirty: true });
            }}
          />
          <Select
            label={t('productEditor.subCategory')}
            value={placement.childId}
            disabled={children.length === 0}
            options={[
              { value: '', label: t('productEditor.noSubCategory') },
              ...children.map((category) => ({ value: category.id, label: nameOf(category) })),
            ]}
            onChange={(event) => {
              setValue('categoryId', event.target.value === '' ? placement.rootId : event.target.value, {
                shouldDirty: true,
              });
            }}
          />
          <Input
            label={t('catalog.reference')}
            helperText={t('productEditor.referenceHelp')}
            required
            errorText={errorText(formState.errors.reference)}
            dir="ltr"
            maxLength={MAX_CODE_LENGTH}
            {...register('reference')}
          />
        </div>
        <OriginField api={api} options={options} index={index} />
      </div>
    </EditorSectionCard>
  );
};
